import {Injectable, NgZone, inject} from '@angular/core';
import {AccountApiService} from './api/routes/account-api.service';
import {AuthService} from './auth.service';
import {Logger} from '../utils/logger';

/** One signed-in device, as the Security page shows it. */
export interface DeviceSession {
  id: string;
  device: string;
  browser: string;
  os: string;
  country: string;
  region: string;
  city: string;
  ip: string;
  /** IANA zone reported by that device, e.g. "Europe/Madrid". May be empty. */
  timeZone: string;
  createdOn: number;
  lastSeenOn: number;
  /** Seen within the online window — "here right now". */
  online: boolean;
  /** The device you are reading this on. */
  current: boolean;
}

/**
 * Where the device id lives.
 *
 * localStorage, not sessionStorage: the id identifies the BROWSER, so it has
 * to survive closing the tab — otherwise every visit looks like a new device
 * and the list fills with duplicates of the same laptop. Exported because
 * AuthService has to carry it across the localStorage wipe on sign-out, for
 * the same reason.
 */
export const DEVICE_SESSION_STORAGE_KEY = 'mm.deviceSessionId';

/** How long a sign-out waits for the server to take the device off its list. */
const END_WAIT_MS = 2000;

/**
 * Tracks which devices are signed in to this account.
 *
 * Firebase Auth keeps no session list — it validates refresh tokens and can
 * revoke them all at once, but cannot say which devices are signed in. So the
 * device mints an id once, stores it, and beats to the server; the server
 * records what it sees (address, geo, user agent) against that id.
 *
 * NOT the same thing as the canvas session limit. That one lives in the
 * Realtime Database, is created per open PROJECT, disappears on disconnect,
 * and exists to cap concurrent editing at two so an account cannot be shared.
 * This is a durable per-DEVICE record for the Security page. They neither read
 * nor write each other's data, and nothing here changes that limit.
 */
@Injectable({providedIn: 'root'})
export class DeviceSessionService {
  private accountApi = inject(AccountApiService);
  private auth = inject(AuthService);
  private ngZone = inject(NgZone);

  /**
   * Comfortably inside the server's five-minute online window, so a single
   * missed beat (a sleeping laptop, one failed request) does not make a device
   * flicker offline in a list someone is reading to spot an intruder.
   */
  private static readonly HEARTBEAT_MS = 60 * 1000;

  private timer: ReturnType<typeof setInterval> | null = null;
  private beating = false;
  /** Whose beat the timer is keeping. */
  private startedFor: string | null = null;

  /**
   * Every tab of this browser beats for the same device id, so the server
   * heard the same thing once per open tab. The tabs now tell each other
   * when they beat, and a tab whose turn comes within SHARED_BEAT_MS of
   * another's skips its own. Nothing is stored: a frozen or closed tab simply
   * stops telling, and the next tab's timer beats as before.
   */
  private static readonly SHARED_BEAT_MS = 50 * 1000;
  private lastBeatInBrowser = 0;
  private channel: BroadcastChannel | null = null;

  /**
   * This device's IANA zone, e.g. "Europe/Madrid".
   *
   * Sent on every beat rather than only at creation, so a device that travels
   * — or whose owner corrects a wrong clock setting — reports where it is now.
   * The server validates it and drops anything Intl does not recognise.
   */
  private static timeZone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch {
      // Ancient or locked-down engines. No local time is fine; a wrong one
      // is not, so there is nothing to fall back to here.
      return '';
    }
  }

  /** Stable per browser profile; created on first use. */
  get sessionId(): string {
    let id = '';
    try {
      id = localStorage.getItem(DEVICE_SESSION_STORAGE_KEY) ?? '';
      if (!id) {
        id = DeviceSessionService.newId();
        localStorage.setItem(DEVICE_SESSION_STORAGE_KEY, id);
      }
    } catch {
      // Private browsing can refuse storage. A per-tab id still gives a
      // working list; it just cannot recognise the device on a later visit.
      id = id || DeviceSessionService.newId();
    }
    return id;
  }

  /**
   * The server refused this device's token. After "Sign out everywhere" (or a
   * password change, or a suspension) that is because the session was revoked
   * -- the beat cannot even reach the device list to be told so, and the tab
   * carried on until its token expired, up to an hour. A forced refresh asks
   * Firebase directly: if the session is gone, sign out now. Anything else
   * (offline, a slow clock) is left for the next beat.
   */
  private async checkStillSignedIn() {
    const user = this.auth.currentUser;
    if (!user) {
      return;
    }
    try {
      await user.getIdToken(true);
    } catch (e) {
      const code = e?.code || '';
      if (code === 'auth/user-token-expired' || code === 'auth/user-disabled'
        || code === 'auth/user-not-found' || code === 'auth/invalid-user-token') {
        this.stop();
        Logger.log('This device\'s session was revoked');
        this.forgetId();
        this.ngZone.run(() => this.auth.signOut());
      }
    }
  }

  /** Drop this browser's id, so the next sign-in mints a new one. */
  private forgetId() {
    try {
      localStorage.removeItem(DEVICE_SESSION_STORAGE_KEY);
    } catch {
      // Storage refused: the per-call id in `sessionId` is already fresh.
    }
  }

  private static newId(): string {
    const c: any = (typeof crypto !== 'undefined') ? crypto : null;
    if (c?.randomUUID) {
      return c.randomUUID();
    }
    if (c?.getRandomValues) {
      const bytes = c.getRandomValues(new Uint8Array(16));
      return Array.from(bytes, (b: number) => b.toString(16).padStart(2, '0')).join('');
    }
    return `d_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }

  /**
   * Begin beating. Safe to call more than once: AuthService calls it on every
   * update of the user's document, and each call used to restart the timer
   * with an extra beat. Only a different account restarts it.
   */
  start() {
    const uid = this.auth.currentUser?.uid || null;
    if (this.timer && this.startedFor === uid) {
      return;
    }
    this.stop();
    this.startedFor = uid;
    this.listenToOtherTabs();
    this.beatUnlessAnotherTabDid();
    // Outside Angular: a 60-second timer that triggered change detection would
    // wake the whole app up forever for something with no visible output.
    this.ngZone.runOutsideAngular(() => {
      this.timer = setInterval(() => this.beatUnlessAnotherTabDid(), DeviceSessionService.HEARTBEAT_MS);
    });
  }

  private beatUnlessAnotherTabDid() {
    if (Date.now() - this.lastBeatInBrowser < DeviceSessionService.SHARED_BEAT_MS) {
      return;
    }
    this.beat();
  }

  private listenToOtherTabs() {
    if (this.channel || typeof BroadcastChannel === 'undefined') {
      return;
    }
    try {
      this.channel = new BroadcastChannel('mm-device-heartbeat');
      this.channel.onmessage = (event: MessageEvent) => {
        const data = event.data || {};
        if (data.sessionId === this.sessionId && typeof data.at === 'number') {
          this.lastBeatInBrowser = Math.max(this.lastBeatInBrowser, data.at);
        }
      };
    } catch {
      // No channel: every tab beats, as before.
      this.channel = null;
    }
  }

  /**
   * This device is signing out: tell the server, so it leaves the device list.
   *
   * Without it a signed-out browser stayed on Profile -> Security as "Signed in
   * since..." until its record expired (the list cannot sign out the device
   * it is on). Needs the sign-in it is ending -- the call carries its token --
   * so it runs before the Firebase sign-out, and must never hold that up: it
   * waits at most END_WAIT_MS and swallows every failure. The device id is
   * kept, so the next sign-in here is this device again, not a new one.
   */
  async end() {
    this.stop();
    let id = '';
    try {
      id = localStorage.getItem(DEVICE_SESSION_STORAGE_KEY) ?? '';
    } catch {
      // Storage refused: there is no record this browser could name.
    }
    if (!id) {
      return;
    }
    const call = this.accountApi.endSession(id)
      .catch(err => Logger.error('Ending the device session failed', err));
    await Promise.race([call, new Promise(resolve => setTimeout(resolve, END_WAIT_MS))]);
  }

  stop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.startedFor = null;
  }

  /**
   * Tell the server this device is still here, and find out whether it has
   * been signed out from somewhere else. Safe to call at any time; concurrent
   * calls collapse into one.
   */
  async beat() {
    if (this.beating) {
      return;
    }
    this.beating = true;
    try {
      // Told before sending, so the other tabs skip this round.
      const at = Date.now();
      this.lastBeatInBrowser = at;
      try {
        this.channel?.postMessage({sessionId: this.sessionId, at});
      } catch {
        // A closed channel: the others just beat themselves.
      }
      const result = await this.accountApi.sessionHeartbeat(this.sessionId, DeviceSessionService.timeZone());
      if (result?.revoked) {
        // Signed out from another device. Stop first, so the sign-out cannot
        // race another beat that would recreate the record.
        this.stop();
        Logger.log('This device was signed out from elsewhere');
        // That id is finished. AuthService.signOut keeps the device id across
        // an ordinary sign-out, and the next sign-in here reused the revoked
        // one: the server answered "revoked" again and this browser was
        // signed out seconds after every sign-in. Forgotten first, the next
        // sign-in starts a new device session. (The server also lets a sign-in
        // newer than the revocation start afresh, for browsers that never got
        // to see this answer.)
        this.forgetId();
        this.ngZone.run(() => this.auth.signOut());
      }
    } catch (err) {
      // Never let session bookkeeping break the app or sign anyone out on a
      // hiccup. A failed beat simply means the device goes quiet in the list
      // until the next one succeeds.
      Logger.error('Session heartbeat failed', err);
      if (err?.msg?.errorCode === 'authentication_failed') {
        await this.checkStillSignedIn();
      }
    } finally {
      this.beating = false;
    }
  }

  // Both pass this device's id so the server can mark which row is "this
  // device" and refuse to let the list sign itself out.

  /**
   * The devices signed in to this account.
   *
   * Beats first. The session record is created by the heartbeat, and the timer
   * starts when the app does — so opening the Security page early enough could
   * list the account before this device had ever registered, and show a page
   * about your devices with your device missing from it. Beating first makes
   * the current device's presence in its own list a certainty rather than a
   * matter of timing. A failed beat is ignored: a stale list beats none.
   */
  async listSessions() {
    await this.beat();
    return this.accountApi.listSessions(this.sessionId);
  }

  revokeSession(id: string) {
    return this.accountApi.revokeSession(id, this.sessionId);
  }

  revokeOtherSessions() {
    return this.accountApi.revokeSessions(this.sessionId);
  }
}
